import { lens } from "@dhmk/zustand-lens";

export type CodeEditorDraftEntry = {
  content: string;
  createIfNotExists?: boolean;
};

type DraftMap = Record<string, CodeEditorDraftEntry>;

type CodeEditorState = {
  drafts: DraftMap;
};

type SetDraftOptions = {
  createIfNotExists?: boolean;
};

type CodeEditorActions = {
  setDraft: (path: string, content: string, options?: SetDraftOptions) => void;
  clearDraft: (path: string) => void;
  clearAll: () => void;
  getDraft: (path: string) => CodeEditorDraftEntry | undefined;
  listDrafts: () => Array<{
    path: string;
    content: string;
    createIfNotExists?: boolean;
  }>;
  reset: () => void;
};

const defaultCodeEditorState: CodeEditorState = {
  drafts: {},
};

export type CodeEditorSlice = CodeEditorState & CodeEditorActions;

export const codeEditorSlice = lens<CodeEditorSlice>((set, get) => ({
  ...defaultCodeEditorState,
  setDraft: (path, content, options) => {
    const current = get().drafts[path];
    if (
      current?.content === content &&
      options?.createIfNotExists === undefined
    ) {
      return;
    }

    const createIfNotExists =
      options?.createIfNotExists ?? current?.createIfNotExists ?? false;

    set({
      drafts: {
        ...get().drafts,
        [path]: {
          content,
          ...(createIfNotExists ? { createIfNotExists: true } : {}),
        },
      },
    });
  },
  clearDraft: (path) => {
    const drafts = get().drafts;
    if (!(path in drafts)) {
      return;
    }

    const nextDrafts = { ...drafts };
    delete nextDrafts[path];
    set({ drafts: nextDrafts });
  },
  clearAll: () => set({ drafts: {} }),
  getDraft: (path) => get().drafts[path],
  listDrafts: () =>
    Object.entries(get().drafts).map(([path, draft]) => ({
      path,
      content: draft.content,
      createIfNotExists: draft.createIfNotExists,
    })),
  reset: () => {
    set({ ...defaultCodeEditorState });
  },
}));
